import { Component, OnInit, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CardModule } from 'primeng/card';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SummaryWrapper } from '@app/wrappers/summary-wrapper/summary-wrapper';
import { SummaryParent } from '@app/wrappers/summary-parent';
import { HttpClient } from '@angular/common/http';
import { environment } from '@root/src/environments/environment';
import { of, Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageModule } from 'primeng/message';
import { ButtonModule } from 'primeng/button';
import { Router, NavigationEnd } from '@angular/router';
import { AuthService } from '@services/auth.service';
import {ThemeModule} from '@app/themes/theme-module';
import {IMenuItem} from '@models/i-menu-item';
import {MenuChildrenPipe} from '@app/utils/pipes/menu-children.pipe';

interface SatelliteSummaryResponse {
  totalProducts: number;
  sentinel2Count: number;
  landsatCount: number;
  lastUpdateUtc: string;
}

@Component({
  selector: 'app-satellite-data',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    TranslateModule,
    ThemeModule,
    ProgressSpinnerModule,
    MessageModule,
    ButtonModule,
    MenuChildrenPipe,
  ],
  templateUrl: './satellite-data.component.html',
  styleUrl: './satellite-data.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SatelliteDataComponent extends SummaryParent implements OnInit, OnDestroy {
  summaryData: SatelliteSummaryResponse | null = null;
  loading: boolean = true;
  error: string | null = null;

  items: IMenuItem[] = [];
  isSubmenu: boolean = false;

  private routerSubscription: Subscription = new Subscription();

  constructor(
    private translateService: TranslateService,
    private http: HttpClient,
    private router: Router,
    private authService: AuthService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadSummaryData();
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.updateMenuState();
      }
    });
    this.updateMenuState();
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  goBack(): void {
    this.router.navigate(['/satellite-data']);
  }

  private updateMenuState(): void {
    this.isSubmenu = this.router.url.includes('/satellite-data/admin');
    if (this.isSubmenu) {
      this.items = [
        {
          icon: 'assets/icons/main/light/licon-ead-icon.svg',
          iconBack: 'assets/icons/main/light/user-icon-line-chart-fill.svg',
          title: 'Logging',
          url: '/satellite-images-admin',
          cardStyle: { background: '#B48EFFCC' },
        },
        {
          icon: 'assets/icons/main/light/task-ead-icon.svg',
          iconBack: 'assets/icons/main/light/task-icon-line-chart-fill.svg',
          title: 'Task Scheduler',
          url: '/satellite-data/scheduler',
          cardStyle: { background: '#6DDE89' },
        },
      ];
    } else {
      this.items = [
        {
          icon: 'assets/icons/main/light/ico-satellite-summary.svg',
          iconBack: 'assets/icons/main/light/ico-download-satellite-data-back.svg',
          title: 'Satellite Images',
          url: '/satellite-images',
          cardStyle: { background: '#0088FFB2' },
        },
        ...(this.authService.isAdmin()
          ? [
              {
                icon: 'assets/icons/main/light/upload-imgs.svg',
                iconBack: 'assets/icons/main/light/upload-img-bg.svg',
                title: 'Administration',
                url: '/satellite-data/admin',
                cardStyle: { background: '#ffd859' },
              } as IMenuItem,
            ]
          : []),
      ];
    }
  }

  loadSummaryData(): void {
    this.loading = true;
    this.error = null;

    const url = `${environment.api}/common/SatelliteProduct/Summary`;
    this.http
      .get<SatelliteSummaryResponse>(url)
      .pipe(
        catchError((err: any) => {
          console.warn('Fallback to GetTotalCount:', err);
          return this.getSummaryFallback();
        }),
      )
      .subscribe({
        next: (data: any) => {
          this.summaryData = data;
          this.loading = false;
        },
        error: (err: any) => {
          this.error = this.translateService.instant('Failed to load satellite summary');
          this.loading = false;
        },
      });
  }

  private getSummaryFallback(): any {
    const totalUrl = `${environment.api}/common/SatelliteProduct/GetTotalCount`;
    return this.http.post<number>(totalUrl, {}).pipe(
      catchError(() => of(0)),
      map((total) => ({
        totalProducts: total,
        sentinel2Count: 0,
        landsatCount: 0,
        lastUpdateUtc: new Date().toISOString(),
      })),
    );
  }
}
